import { lazy, Suspense } from 'react';
import { createBrowserRouter, Navigate, Outlet } from 'react-router-dom';
import { AuthProvider } from '@/features/auth/hooks/useAuth';
import { ROLES } from '@/features/auth/roles';
import {
  ProtectedRoute,
  PublicOnlyRoute,
  RequireAuth,
} from '@/features/auth/components/ProtectedRoute';
import { AccountLayout, AdminLayout, VendorLayout } from '@/layouts/AppLayout';
import { StorefrontLayout } from '@/features/storefront/components/StorefrontLayout';
import { RouteError } from '@/app/RouteError';
import { RouteFallback } from '@/ui/RouteFallback';

/**
 * Route configuration as data, not JSX soup.
 *
 * The reference project declared every route inline in a single 1,050-line
 * App.jsx with no code splitting, so the storefront shipped the admin console
 * to every anonymous visitor. Here each audience is its own lazy chunk.
 */

const StorefrontHome = lazy(() => import('@/features/storefront/pages/HomePage'));
const AuthPage = lazy(() => import('@/features/auth/pages/AuthPage'));
const VerifyEmailPage = lazy(() => import('@/features/auth/pages/VerifyEmailPage'));
const ChangePasswordPage = lazy(() => import('@/features/auth/pages/ChangePasswordPage'));
const RegisterStorePage = lazy(() => import('@/features/auth/pages/RegisterStorePage'));
const AdminDashboard = lazy(() => import('@/features/admin/pages/AdminDashboard'));
const AdminUsersPage = lazy(() => import('@/features/admin/pages/AdminUsersPage'));
const AdminSubscriptionPage = lazy(
  () => import('@/features/subscription/pages/AdminSubscriptionPage'),
);
const PlatformOverviewPage = lazy(() => import('@/features/platform/pages/PlatformOverviewPage'));
const PlatformAdminsPage = lazy(() => import('@/features/platform/pages/PlatformAdminsPage'));
const PlatformSubscriptionsPage = lazy(
  () => import('@/features/subscription/pages/PlatformSubscriptionsPage'),
);
const PlatformApiKeysPage = lazy(() => import('@/features/platform/pages/PlatformApiKeysPage'));
const PlatformCustomersPage = lazy(() => import('@/features/platform/pages/PlatformCustomersPage'));
const PlatformCategoriesPage = lazy(
  () => import('@/features/platform/pages/PlatformCategoriesPage'),
);
const VendorDashboard = lazy(() => import('@/features/vendor/pages/VendorDashboard'));
const VendorProductsPage = lazy(() => import('@/features/catalog/pages/VendorProductsPage'));
const VendorProductEditorPage = lazy(
  () => import('@/features/catalog/pages/VendorProductEditorPage'),
);
const AdminCatalogPage = lazy(() => import('@/features/catalog/pages/AdminCatalogPage'));
const AdminProductEditorPage = lazy(
  () => import('@/features/catalog/pages/AdminProductEditorPage'),
);
const ProductListPage = lazy(() => import('@/features/storefront/pages/ProductListPage'));
const ProductDetailPage = lazy(() => import('@/features/storefront/pages/ProductDetailPage'));
const CartPage = lazy(() => import('@/features/cart/pages/CartPage'));
const CheckoutPage = lazy(() => import('@/features/orders/pages/CheckoutPage'));
const OrderDetailPage = lazy(() => import('@/features/orders/pages/OrderDetailPage'));
const InvoicePage = lazy(() => import('@/features/orders/pages/InvoicePage'));
const AccountOrdersPage = lazy(() => import('@/features/orders/pages/AccountOrdersPage'));
const WishlistPage = lazy(() => import('@/features/account/pages/WishlistPage'));
const AdminOrdersPage = lazy(() => import('@/features/orders/pages/AdminOrdersPage'));
const AdminOrderDetailPage = lazy(() => import('@/features/orders/pages/AdminOrderDetailPage'));
const VendorOrdersPage = lazy(() => import('@/features/orders/pages/VendorOrdersPage'));
const AdminVendorsPage = lazy(() => import('@/features/vendors/pages/AdminVendorsPage'));
const AdminVendorDetailPage = lazy(() => import('@/features/vendors/pages/AdminVendorDetailPage'));
const VendorOnboardingPage = lazy(() => import('@/features/vendors/pages/VendorOnboardingPage'));
const AccountDashboard = lazy(() => import('@/features/account/pages/AccountDashboard'));
const AdminWebsitePage = lazy(() => import('@/features/site/pages/AdminWebsitePage'));
const AdminStorePage = lazy(() => import('@/features/store/pages/AdminStorePage'));
const AdminSeasonsPage = lazy(() => import('@/features/admin/pages/AdminSeasonsPage'));
const NotFoundPage = lazy(() => import('@/features/common/pages/NotFoundPage'));
const HelpCenterPage = lazy(() => import('@/features/common/pages/HelpCenterPage'));
const ContactPage = lazy(() => import('@/features/common/pages/ContactPage'));

/**
 * AuthProvider wraps the whole tree from inside the router, so guards, pages
 * and the sign-out button all share one auth context and can use useNavigate.
 */
function AuthShell() {
  return (
    <AuthProvider>
      <Outlet />
    </AuthProvider>
  );
}

function lazyEl(Component) {
  return (
    <Suspense fallback={<RouteFallback />}>
      <Component />
    </Suspense>
  );
}

export const router = createBrowserRouter([
  {
    element: <AuthShell />,
    // Any page that throws lands here instead of the router's developer
    // screen. Children inherit it, so one entry covers every route.
    errorElement: <RouteError />,
    children: [
      // The shop. Public, and the only tree an anonymous visitor can reach -
      // which is also why it is its own lazy chunk: a shopper never downloads
      // the admin console.
      {
        element: <StorefrontLayout />,
        children: [
          { path: '/', element: lazyEl(StorefrontHome) },
          { path: '/products', element: lazyEl(ProductListPage) },
          { path: '/products/:slug', element: lazyEl(ProductDetailPage) },
          { path: '/cart', element: lazyEl(CartPage) },
          { path: '/checkout', element: lazyEl(CheckoutPage) },
          { path: '/help', element: lazyEl(HelpCenterPage) },
          { path: '/contact', element: lazyEl(ContactPage) },
          // Public: the emailed link may be opened signed out, or on another device.
          { path: '/verify-email', element: lazyEl(VerifyEmailPage) },
          // Not behind RequireAuth: guest checkout is supported, and a guest
          // proves the order is theirs with the email it was placed with.
          { path: '/orders/:orderId', element: lazyEl(OrderDetailPage) },
          { path: '/orders/:orderId/invoice', element: lazyEl(InvoicePage) },
        ],
      },

      {
        element: <PublicOnlyRoute />,
        children: [
          { path: '/login', element: lazyEl(AuthPage) },
          { path: '/register', element: lazyEl(AuthPage) },
          { path: '/register-store', element: lazyEl(RegisterStorePage) },
        ],
      },

      // Any signed-in role, no further gate: an account still holding a
      // generated password is redirected here and can reach nothing else.
      {
        element: <RequireAuth />,
        children: [{ path: '/change-password', element: lazyEl(ChangePasswordPage) }],
      },

      {
        // Running a shop: its vendors, catalog, orders and website. Shop admins
        // only - the operator manages the platform, not any one shop, and is
        // sent to their own home page if they follow a link here.
        element: <ProtectedRoute allowedRoles={[ROLES.ADMIN]} />,
        children: [
          {
            path: '/admin',
            element: <AdminLayout />,
            children: [
              // The shop dashboard: its queue and every link in it point at
              // shop routes. An operator landing on /admin is sent to their
              // own home instead of a page of links that bounce.
              { index: true, element: lazyEl(AdminDashboard) },
              { path: 'vendors', element: lazyEl(AdminVendorsPage) },
              { path: 'vendors/:vendorId', element: lazyEl(AdminVendorDetailPage) },
              { path: 'catalog', element: lazyEl(AdminCatalogPage) },
              // Declared before ':productId' so the literal segment is not
              // swallowed by the parameter - react-router matches in order.
              { path: 'catalog/new', element: lazyEl(AdminProductEditorPage) },
              { path: 'catalog/:productId', element: lazyEl(AdminProductEditorPage) },
              { path: 'orders', element: lazyEl(AdminOrdersPage) },
              { path: 'orders/:orderId', element: lazyEl(AdminOrderDetailPage) },
              { path: 'seasons', element: lazyEl(AdminSeasonsPage) },
              { path: 'website', element: lazyEl(AdminWebsitePage) },
              // Everyone who signs in to this shop. The operator has Shop
              // admins instead: one role, across every shop.
              { path: 'users', element: lazyEl(AdminUsersPage) },
              { path: 'subscription', element: lazyEl(AdminSubscriptionPage) },
            ],
          },
        ],
      },

      {
        // Nested under /admin so an operator works in one shell, but guarded
        // separately: a tenant admin reaching this would see every customer.
        element: <ProtectedRoute allowedRoles={[ROLES.SUPER_ADMIN]} />,
        children: [
          {
            path: '/admin',
            element: <AdminLayout />,
            children: [
              // The operator's home: how every shop is doing. Its own path, not
              // the /admin index - that belongs to the shop admin's guard, which
              // sends an operator here.
              { path: 'overview', element: lazyEl(PlatformOverviewPage) },
              { path: 'customers', element: lazyEl(PlatformCustomersPage) },
              // Shared by every shop, so only the operator edits it.
              { path: 'categories', element: lazyEl(PlatformCategoriesPage) },
              // The platform store page at the bare domain. Operator only - a
              // tenant admin edits their own website, never this.
              { path: 'store', element: lazyEl(AdminStorePage) },
              { path: 'api-keys', element: lazyEl(PlatformApiKeysPage) },
              { path: 'admins', element: lazyEl(PlatformAdminsPage) },
              { path: 'subscriptions', element: lazyEl(PlatformSubscriptionsPage) },
            ],
          },
        ],
      },

      {
        element: <ProtectedRoute allowedRoles={[ROLES.VENDOR]} />,
        children: [
          {
            path: '/vendor',
            element: <VendorLayout />,
            children: [
              { index: true, element: lazyEl(VendorDashboard) },
              { path: 'products', element: lazyEl(VendorProductsPage) },
              { path: 'products/new', element: lazyEl(VendorProductEditorPage) },
              { path: 'products/:productId', element: lazyEl(VendorProductEditorPage) },
              { path: 'orders', element: lazyEl(VendorOrdersPage) },
              { path: 'onboarding', element: lazyEl(VendorOnboardingPage) },
            ],
          },
        ],
      },

      {
        element: <ProtectedRoute allowedRoles={[ROLES.CUSTOMER]} />,
        children: [
          {
            path: '/account',
            element: <AccountLayout />,
            children: [
              { index: true, element: lazyEl(AccountDashboard) },
              { path: 'orders', element: lazyEl(AccountOrdersPage) },
              { path: 'wishlist', element: lazyEl(WishlistPage) },
            ],
          },
        ],
      },

      { path: '/404', element: lazyEl(NotFoundPage) },
      { path: '*', element: <Navigate to="/404" replace /> },
    ],
  },
]);
